import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { canClientViewFunctionality } from 'FielderUtils/taskDetail/taskDetailUtil';
import { FielderSmartNav } from '~/FielderElements/Utils/SmartTabNav';
/**
 * Props:
 * - lan: { information, execution, agent, documents, photos, activity, functionality, route }
 * - changeTab: (id: string) => void
 * - modules: any (used by canClientViewFunctionality)
 * - selectedTab / current (optional): currently active tab id (string)
 * - isActive? (optional legacy): fn(tabId) -> returns class w/ 'active'
 */
const ALL_IDS = ['info', 'exe', 'agent', 'document', 'pics', 'act', 'mod', 'modClient', 'route'];
const TaskDetailNavTab = (
  {
    lan,
    changeTab,
    modules,
    selectedTab,
    current,
    isActive,
  }) => {
  const user = useSelector(selectUser);
  const items = useMemo(() => {
    const base = [
      { id: 'info',     label: lan.information },
      { id: 'exe',      label: lan.execution },
      { id: 'agent',    label: lan.agent },
      { id: 'document', label: lan.documents },
      { id: 'pics',     label: lan.photos },
    ];
    if (!RoleUtils.isUserClient(user)) {
      base.push(
        { id: 'act', label: lan.activity },
        { id: 'mod', label: lan.functionality },
        { id: 'route', label: lan.route },
      );
    }
    if (canClientViewFunctionality(user, modules)) {
      base.push({ id: 'modClient', label: lan.functionality });
    }
    // de-dupe just in case
    const seen = new Set();
    return base.filter(it => (seen.has(it.id) ? false : (seen.add(it.id), true)));
  }, [lan, modules, user]);
  // Backward compatibility with legacy "isActive" function
  const inferredFromLegacy =
    typeof isActive === 'function'
      ? ALL_IDS.find(id => (isActive(id) || '').includes('active'))
      : undefined;

  const value = selectedTab || current || inferredFromLegacy || 'info';

  const handleChange = useCallback(
    (id) => {
      changeTab?.(id);
    },
    [changeTab]
  );

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <FielderSmartNav
          items={items}
          value={value}
          onChange={handleChange}
          centered={false}
          variant="fullWidth"
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Paper>
    </Box>
  );
};

TaskDetailNavTab.propTypes = {
  lan: PropTypes.shape({
    information: PropTypes.string,
    execution: PropTypes.string,
    agent: PropTypes.string,
    documents: PropTypes.string,
    photos: PropTypes.string,
    activity: PropTypes.string,
    functionality: PropTypes.string,
    route: PropTypes.string,
  }).isRequired,
  changeTab: PropTypes.func,
  modules: PropTypes.any,
  selectedTab: PropTypes.string,
  current: PropTypes.string,
  isActive: PropTypes.func,
};

export default React.memo(TaskDetailNavTab);
//Original code:
/*import React, { useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '@core/store/modules/session/sessionSelectors';
import RoleUtils from 'FielderUtils/roles/roleUtils';
import { canClientViewFunctionality } from 'FielderUtils/taskDetail/taskDetailUtil';
import './TaskDetails.css';
import { FielderSmartNav } from '~/FielderElements/Utils/SmartTabNav';

const TaskDetailNavTab = (
  {
    lan,
    changeTab,
    modules,
    selectedTab,
    current,
    isActive,
  }) => {
  const user = useSelector(selectUser);
  const items = useMemo(() => {
    const arr = [
      { id: 'info',     label: lan.information },
      { id: 'exe',      label: lan.execution },
      { id: 'agent',    label: lan.agent },
      { id: 'document', label: lan.documents },
      { id: 'pics',     label: lan.photos },
    ];
    if (!RoleUtils.isUserClient(user)) {
      arr.push({ id: 'act', label: lan.activity });
      arr.push({ id: 'mod', label: lan.functionality });
      arr.push({ id: 'route', label: lan.route });
    }
    if (canClientViewFunctionality(user, modules)) {
      arr.push({ id: 'modClient', label: lan.functionality });
    }
    const seen = new Set();
    return arr.filter(it => (seen.has(it.id) ? false : (seen.add(it.id), true)));
  }, [lan, modules, user]);
  const inferredFromLegacy =
    typeof isActive === 'function'
      ? (['info','exe','agent','document','pics','act','mod','modClient','route']
        .find(id => (isActive(id) || '').includes('active')) || undefined)
      : undefined;
  const value = selectedTab || current || inferredFromLegacy || 'info';
  const handleChange = useCallback((id) => {
    changeTab?.(id);
  }, [changeTab]);
  return (
    <Box className="mx-3">
      <Box
        className="td-navbar mb-3"
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <FielderSmartNav
          items={items}
          value={value}
          onChange={handleChange}
          centered={false}
          variant="fullWidth"
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
    </Box>
  );
};
export default React.memo(TaskDetailNavTab);
*/
